import type { Department } from "../contexts/AuthProvider";
import { type BreakRulesConfig, HppdSeparationType, NurseFullRoleShort, type NurseInShift, NurseRole, type StatsPerson } from "./models";
import { getRoleGroupsForRole } from "./roles";
import { filterNursesForStats } from "./statsCalculations";
import { getShiftWorkHours } from "./utilFunctions";

/**
 * HPPD split by a dimension (role, role group or job), the Day view's HPPD breakdown. Pure, so the Day view's
 * hook, the staff table, the gaps view and the custom printout all compute it the same way.
 */

export type HppdBreakdownEntry = { key: string; hppd: number };

function getDimensionKey(nurse: NurseInShift, separationType: HppdSeparationType, chosenDepartment: Department): string {
	switch (separationType) {
		case HppdSeparationType.Role:
			return NurseFullRoleShort[nurse.role] ?? nurse.role;
		case HppdSeparationType.RoleGroups: {
			const groups = getRoleGroupsForRole(nurse.role, chosenDepartment);
			if (groups.length > 0) return groups[0];
			// Fall back to role short code if no group matches
			return NurseFullRoleShort[nurse.role] ?? nurse.role;
		}
		case HppdSeparationType.Jobs:
			return nurse.jobName ?? "Unassigned";
		default:
			return "HPPD";
	}
}

export function computeHppdBreakdown(
	nurses: NurseInShift[],
	census: number,
	departmentId: string,
	breakRulesConfig: BreakRulesConfig[],
	separationType: HppdSeparationType,
	chosenDepartment: Department,
	jobManagementEnabled: boolean = true,
	allNurses?: StatsPerson[],
	unionByNurseId?: Map<number, string>,
): HppdBreakdownEntry[] {
	if (!census) return [];

	const hoursBuckets: Record<string, number> = {};

	for (const nurse of nurses) {
		if (!filterNursesForStats(nurse, jobManagementEnabled, allNurses, false, false)) continue;

		const hours = getShiftWorkHours(
			departmentId,
			nurse.endTime,
			nurse.startTime,
			breakRulesConfig,
			NurseRole[NurseFullRoleShort[nurse.role] as keyof typeof NurseRole],
			nurse.type,
			nurse.jobId ? [nurse.jobId] : [],
			jobManagementEnabled,
			unionByNurseId?.get(nurse.nurseId) ?? "",
		);

		const key = getDimensionKey(nurse, separationType, chosenDepartment);
		hoursBuckets[key] = (hoursBuckets[key] || 0) + hours;
	}

	return Object.entries(hoursBuckets)
		.filter(([, hours]) => hours > 0)
		.sort(([, a], [, b]) => b - a)
		.map(([key, hours]) => ({
			key,
			hppd: Number((hours / census).toFixed(2)),
		}));
}
